import { formatPlainTime, startOfWeekPlainDate } from '@airreading/common';
import { InfoKeyValue } from '@/components/info-key-value.js';
import {
  adjustClassTimeForWeeklyDisplay,
  upcomingWeeklyClassTimeChanges,
  type MinimalClassTime,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting.js';

export function WeeklyClassTimes({
  classTime,
  sourceTimeZone,
  displayTimeZone,
  testId,
  nextSessionAtInstant,
  nextSessionEndAtInstant,
}: {
  classTime: MinimalClassTime;
  sourceTimeZone: string;
  displayTimeZone: string;
  testId: string;
  nextSessionAtInstant?: Temporal.Instant | null;
  nextSessionEndAtInstant?: Temporal.Instant | null;
}) {
  // Capture once so the label and notice cannot straddle different reference weeks.
  const now = Temporal.Now.instant();
  const thisWeekStart = startOfWeekPlainDate(now.toZonedDateTimeISO(sourceTimeZone).toPlainDate());
  const nextSessionWeekStart = nextSessionAtInstant
    ? startOfWeekPlainDate(nextSessionAtInstant.toZonedDateTimeISO(sourceTimeZone).toPlainDate())
    : thisWeekStart;
  const isFutureWeek = Temporal.PlainDate.compare(nextSessionWeekStart, thisWeekStart) > 0;
  const referenceInstant = isFutureWeek && nextSessionAtInstant ? nextSessionAtInstant : now;
  const isDatedOneTime = classTime.type === 'one_time' && nextSessionAtInstant && nextSessionEndAtInstant;
  const nextStart = nextSessionAtInstant?.toZonedDateTimeISO(displayTimeZone);
  const nextEnd = nextSessionEndAtInstant?.toZonedDateTimeISO(displayTimeZone);
  const formatDate = (date: Temporal.PlainDate) =>
    date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const classTimes =
    isDatedOneTime && nextStart && nextEnd
      ? `${formatDate(nextStart.toPlainDate())}, ${formatPlainTime(nextStart.toPlainTime())} - ${
          nextStart.toPlainDate().equals(nextEnd.toPlainDate()) ? '' : `${formatDate(nextEnd.toPlainDate())}, `
        }${formatPlainTime(nextEnd.toPlainTime())}`
      : formatStudentClassTimeAbbr(
          adjustClassTimeForWeeklyDisplay(classTime, sourceTimeZone, displayTimeZone, referenceInstant),
        );
  const label =
    classTime.type === 'one_time'
      ? 'Class times'
      : isFutureWeek && nextStart
        ? `Regular class times starting ${formatDate(nextStart.toPlainDate())}`
        : 'Regular class times';
  const changes = upcomingWeeklyClassTimeChanges(classTime, sourceTimeZone, displayTimeZone, referenceInstant);

  return (
    <InfoKeyValue
      label={
        <>
          {label} <span className="font-medium">({displayTimeZone})</span>
        </>
      }
      className="mr-4"
      value={
        <>
          <p>{classTimes}</p>
          {changes.length > 0 && (
            <div
              className="mt-2 space-y-2 rounded-md bg-blue-50 p-2 text-sm text-blue-900"
              data-testid={`${testId}-time-change-notice`}
            >
              {changes.map((change) => (
                <p key={`${change.firstChangedClassPlainDate}-${change.meetingBlocks[0].startPlainTime}`}>
                  Starting{' '}
                  {change.firstChangedClassPlainDate.toLocaleString('en-US', { month: 'long', day: 'numeric' })}:{' '}
                  {formatStudentClassTimeAbbr({ type: 'recurring', meetingBlocks: change.meetingBlocks })} (
                  {displayTimeZone}), because of the daylight saving time change.
                </p>
              ))}
            </div>
          )}
        </>
      }
      data-testid={testId}
    />
  );
}
